import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import styles from "./SignupPage.module.css";
import bgImage from "../../assets/images/bg-login.jpeg";
import api from "../../services/api";
import useAuthStore from "../../store/authStore";
import { ENDPOINTS } from "../../utils/endpoints";

const SignupPage = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    device_token: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const loginUser = useAuthStore((state) => state.login);
  const navigate = useNavigate();

  const handleChange = (field) => (e) =>
    setFormData({ ...formData, [field]: e.target.value });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSignup();
    }
  };

  // Password validation
  const validatePassword = (password) => {
    const rules = [
      {
        regex: /.{8,}/,
        message: "Password should be at least 8 characters long",
      },
      {
        regex: /[A-Z]/,
        message: "Password should contain at least one uppercase letter",
      },
      {
        regex: /[a-z]/,
        message: "Password should contain at least one lowercase letter",
      },
      {
        regex: /[0-9]/,
        message: "Password should contain at least one number",
      },
      {
        regex: /[!@#$%^&*(),.?":{}|<>]/,
        message: "Password should contain at least one special character",
      },
    ];

    for (let rule of rules) {
      if (!rule.regex.test(password)) {
        return rule.message;
      }
    }
    return null;
  };

  // Email validation
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email) ? null : "Enter a valid email address";
  };

  // Unified validation before signup
  const validateForm = () => {
    if (!formData.username.trim()) return "Enter Username";
    if (!formData.email.trim()) return "Enter Email";
    const emailError = validateEmail(formData.email);
    if (emailError) return emailError;

    if (!formData.password) return "Enter Password";
    const passwordError = validatePassword(formData.password);
    if (passwordError) return passwordError;

    if (!formData.first_name.trim()) return "Enter First Name";
    if (!formData.last_name.trim()) return "Enter Last Name";

    return null;
  };

  const handleSignup = async () => {
    setError("");

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post(ENDPOINTS.SIGNUP, formData);

      if (data.success) {
        await handleLogin(); // login after signup
      } else {
        setError(data.message || "Signup failed");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      const deviceToken = "your-device-token-placeholder"; // TODO: replace with FCM logic

      const { data } = await api.post(ENDPOINTS.LOGIN, {
        username: formData.username,
        password: formData.password,
        device_token: deviceToken,
      });

      if (data.success) {
        loginUser(data.accessToken, data.user, data.user_meta);
        navigate("/dashboard");
      } else {
        setError(data.message || "Login failed");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <div className={styles.container}>
      {loading && <div className={styles.loadingOverlay}>Please wait...</div>}

      <div className={styles.left}>
        <img src={bgImage} alt="signup-bg" className={styles.bgImage} />
        {/* <div className={styles.logo}>EAU</div> */}
        {/* <p className={styles.subTitle}>Ecolearn & Alpha Universe Group</p> */}
      </div>

      <div className={styles.right}>
        <h2 className={styles.title}>SIGN UP</h2>

        <InputField
          icon={<PersonIcon className={styles.icon} />}
          placeholder="Username"
          value={formData.username}
          onChange={handleChange("username")}
          onKeyPress={handleKeyPress}
        />

        <InputField
          icon={<EmailIcon className={styles.icon} />}
          placeholder="Email"
          value={formData.email}
          onChange={handleChange("email")}
          onKeyPress={handleKeyPress}
        />

        <InputField
          icon={<LockIcon className={styles.icon} />}
          placeholder="Password"
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleChange("password")}
          onKeyPress={handleKeyPress}
          showPasswordToggle={true}
          showPassword={showPassword}
          onTogglePassword={togglePasswordVisibility}
        />

        <InputField
          placeholder="First Name"
          value={formData.first_name}
          onChange={handleChange("first_name")}
          onKeyPress={handleKeyPress}
        />

        <InputField
          placeholder="Last Name"
          value={formData.last_name}
          onChange={handleChange("last_name")}
          onKeyPress={handleKeyPress}
        />

        <button
          className={styles.loginBtn}
          onClick={handleSignup}
          disabled={loading}
        >
          {loading ? "Creating..." : "Create Account"}
        </button>

        {error && <p className={styles.error}>{error}</p>}

        <p className={styles.signup} onClick={() => navigate("/")}>
          Back to Login
        </p>
      </div>
    </div>
  );
};

const InputField = ({
  icon,
  placeholder,
  type = "text",
  value,
  onChange,
  onKeyPress,
  showPasswordToggle = false,
  showPassword = false,
  onTogglePassword,
}) => (
  <div className={styles.inputGroup}>
    {icon && icon}
    <input
      type={type}
      placeholder={placeholder}
      className={styles.input}
      value={value}
      onChange={onChange}
      onKeyPress={onKeyPress}
    />
    {showPasswordToggle && (
      <button
        type="button"
        className={styles.passwordToggle}
        onClick={onTogglePassword}
        tabIndex={-1}
      >
        {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </button>
    )}
  </div>
);

export default SignupPage;
